import React, { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import Button from "@components/buttons/Button/Button";
import styles from "@styles/CommonAuthPageLayout.module.css";
import { isValidPassword } from "@util/validators";
import { setNewPassword } from "@services/authService";

const INVALID_PASSWORD_MESSAGE = "Invalid password. Please try again.";

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("userId");
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [resetError, setResetError] = useState("");
    const [loading, setLoading] = useState(false); // Loading state

    // Validation function for password
    const validatePassword = useCallback((val: string) => {
        return isValidPassword(val);
    }, []);

    const validateConfirmPassword = useCallback((val: string) => {
        if (val !== password) {
            return "Passwords do not match.";
        }
        return "";
    }, [password]);

    // Handlers for password input with validation
    const onPasswordChange = useCallback((val: string) => {
        setPassword(val);
        setPasswordError(validatePassword(val));
    }, [validatePassword]);

    const onConfirmPasswordChange = useCallback((val: string) => {
        setConfirmPassword(val);
        setConfirmPasswordError(validateConfirmPassword(val));
    }, [validateConfirmPassword]);

    const onPasswordBlur = useCallback(() => {
        setPasswordError(validatePassword(password));
        setConfirmPasswordError(validateConfirmPassword(confirmPassword));
    }, [confirmPassword, password, validateConfirmPassword, validatePassword]);

    const onConfirmPasswordBlur = useCallback(() => {
        setConfirmPasswordError(validateConfirmPassword(confirmPassword));
    }, [confirmPassword, validateConfirmPassword]);

    const handleSubmit = async () => {
        let isValid = true;

        // Validate Password
        const passwordValidationError = validatePassword(password);
        if (passwordValidationError) {
            setPasswordError(passwordValidationError);
            isValid = false;
        }

        const passwordConfirmValidationError = validateConfirmPassword(confirmPassword);
        if (passwordConfirmValidationError) {
            setConfirmPasswordError(passwordConfirmValidationError);
            isValid = false;
        }

        if (isValid) {
            setResetError("");
            setLoading(true); // Show loading
            if (!userId || !token) {
                throw new Error("No Token.");
            }
            try {
                // Simulate the API call for password reset
                await setNewPassword(userId, token, password);

                // Navigate to confirmation page after success
                navigate("../reset-confirmation");
            } catch (error) {
                setResetError(INVALID_PASSWORD_MESSAGE);
            } finally {
                setLoading(false); // Hide loading
            }
        } else {
            setResetError(INVALID_PASSWORD_MESSAGE);
        }
    };

    return (
        <div>
            <Card className={styles.cardContainer}>
                <TextInput
                    label="Password"
                    id="password"
                    value={password}
                    onChange={onPasswordChange}
                    onBlur={onPasswordBlur}
                    placeholder="Enter new password..."
                    type="password"
                    errorMessage={passwordError}
                    disabled={loading} // Disable while loading
                />
                <TextInput
                    label="Confirm Password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={onConfirmPasswordChange}
                    onBlur={onConfirmPasswordBlur}
                    placeholder="Confirm password..."
                    type="password"
                    errorMessage={confirmPasswordError}
                    disabled={loading} // Disable while loading
                />
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading} // Disable button during loading
                >
                    {loading ? "Submitting..." : "Submit"}
                    {" "}
                    {/* Show loading text */}
                </Button>

                {resetError && (
                    <p className={styles.errorMessage}>{resetError}</p>
                )}
            </Card>
        </div>
    );
};

export default ResetPasswordPage;
